import { Injectable } from '@angular/core'
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http'

// RxJS
import { BehaviorSubject, Observable } from 'rxjs'
import { BaseService } from './base.service'

// Models
import { CareGiver } from '../models/caregiver'
import { tap } from 'rxjs/operators'
import { CareGiverFilter, PageInfo, Pagination } from '../models'
import { MiscService } from '../utilities/misc.service'


@Injectable({
  providedIn: 'root',
})
export class CareGiverService extends BaseService {

  private careGiversSubject = new BehaviorSubject<CareGiver[]>([]);
  private careGiversData: { data: CareGiver[] } = { data: [] };
  public readonly careGiversObs = this.careGiversSubject.asObservable();

  // Page info
  private pageInfoSubject = new BehaviorSubject<PageInfo>(null);
  private pageInfoData: { data: PageInfo } = { data: null };
  public readonly pageInfoObs = this.pageInfoSubject.asObservable();


  constructor(
    private http: HttpClient,
    private miscService: MiscService,) {
    super()
  }

  setDataObs(data: CareGiver[] = []) {
    this.careGiversData.data = data;
    this.careGiversSubject.next(Object.assign({}, this.careGiversData).data);
  }

  setPageObs(pageInfo: PageInfo) {
    this.pageInfoData.data = pageInfo;
    this.pageInfoSubject.next(Object.assign({}, this.pageInfoData).data);
  }

  // Load data & populate
  load(offset: number = null, limit: number = null, filter: CareGiverFilter) {
    this.getList(offset, limit, filter).subscribe();
  }

  loadCaregivers(
    filter: CareGiverFilter,
    pagination: Pagination,
  ): Observable<HttpResponse<CareGiver[]>> {
      const offset = pagination.offset;
      const limit = pagination.limit;

      const name = filter.Name || '';
      const geoId = filter.GeoId || 0;
      const geoType = filter.GeoType || "county";
      const distmi = filter.Distmi || 25;
  
      // Declare
      let httpParams = new HttpParams()
        .set('name', `${name}`)
        .set('id', `${geoId}`)
        .set('geotype', `${geoType}`)
        .set('distmi', `${distmi}`)
  
      // Pagination params: offset, limit
      if (offset) httpParams = httpParams.set('offset', `${offset}`);
  
      // Limit is Page size, items per page
      if (limit) httpParams = httpParams.set('limit', `${limit}`);
  
      const apiUrl = this.baseUrlCare + `/search/caregivers`;
      
      return this.http.get<CareGiver[]>(apiUrl, { withCredentials: true, params: httpParams, observe: 'response' });
  }

  getList(offset: number = null, limit: number = null, filter: CareGiverFilter) {
    const name = filter.Name || '';
    const geoId = filter.GeoId || 0;
    const geoType = filter.GeoType || "county";
    const distmi = filter.Distmi || 25;

    // Array to string comma separated
    const languages: any = filter.Languages ? filter.Languages.map(l => l.Id).join() : null;

    // Declare
    let httpParams = new HttpParams()
      .set('name', `${name}`)
      .set('id', `${geoId}`)
      .set('geotype', `${geoType}`)
      .set('distmi', `${distmi}`);

    // Other params
    if (languages) httpParams = httpParams.set('languages', `${languages}`);

    // Pagination params: offset, limit
    if (offset) httpParams = httpParams.set('offset', `${offset}`);

    // Limit is Page size, items per page
    if (limit) httpParams = httpParams.set('limit', `${limit}`);

    const apiUrl = this.baseUrlCare + `/search/caregivers`;
    return this.http.get<CareGiver[]>(apiUrl, { params: httpParams, observe: 'response',withCredentials:true}).pipe(tap(response => {
      const totalRecords = +response.headers.get("Total-Records") || 0;
      const facilities: CareGiver[] = response.body || [];
      const pageNumber = offset / limit + 1;

      const pageInfo: PageInfo = this.miscService.getPager(totalRecords, pageNumber, limit);

      this.setDataObs(facilities);
      this.setPageObs(pageInfo);

      return response;
    }));
  }

  getById(id: number): Observable<CareGiver> {
    const apiUrl = this.baseUrlCare + `/CareGiver/${id}`;
      return this.http.get<CareGiver>(apiUrl, { withCredentials: true });
  }
}
